import { inject, Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, of, switchMap } from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { SEARCH_AND_FILTERS_CONDITION_PREFIX } from '@blaze-runtime/engine-domain';
import {
  ComparisonOperator,
  ComparisonQueryNode,
  DisplayInfo,
  ExecuteWorkflowResponse,
  LogicalQueryNode,
  LogicalType,
  ServerErrorResponse,
} from '@blaze-shared/api-contracts-domain';
import {
  getAllComparisonNodes,
  mapSimpleQueryConditionsToComplexFilters,
} from '@blaze-shared/datasets-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn } from '@blaze-shared/schema-domain';
import {
  ApiCallWorkflowNode,
  GetWorkflowNode,
  isApiCallNode,
  isGetNode,
  Workflow,
} from '@blaze-shared/workflow-domain';

import { findWorkflowSourceNode } from '../canvas-elements/element-model/data-binding';

interface WorkflowDisplayData {
  name: string;
  modified?: boolean;
  custom?: boolean;
  sourceNode?: GetWorkflowNode | ApiCallWorkflowNode;
}

@Injectable({ providedIn: 'root' })
export class WorkflowUtilsService {
  private readonly actions$ = inject(Actions);
  private readonly iss = inject(IntelliSenseService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  /** @param workflowId string or first get Node */
  areAnyFiltersApplied(
    workflowId: string | Observable<GetWorkflowNode | undefined>,
  ): Observable<boolean> {
    const firstGetNode$: Observable<GetWorkflowNode | undefined> =
      typeof workflowId === 'string'
        ? this.store.select(runtimeSelectors.selectFirstNodeInWorkflow(workflowId, isGetNode))
        : workflowId;

    return firstGetNode$.pipe(
      map((getNode) => {
        const hasSimpleFilters =
          getNode && getNode.datasetId ? getNode.queryConditions?.length > 0 : false;
        const hasComplexFilters = !!getNode?.query?.children?.length;

        return hasSimpleFilters || hasComplexFilters;
      }),
    );
  }

  /** @param workflowIdOrFirstGetNode string or first get Node */
  getGeneratedFilters(
    workflowIdOrFirstGetNode: string | Observable<GetWorkflowNode | undefined>,
  ): Observable<
    {
      column: string;
      description: string;
      displayInfos: DisplayInfo[];
      isFormulaValue: boolean;
      operator: ComparisonOperator;
      original: ComparisonQueryNode;
      value: string | undefined;
    }[]
  > {
    const firstGetNode$: Observable<GetWorkflowNode | undefined> =
      typeof workflowIdOrFirstGetNode === 'string'
        ? this.store.select(
            runtimeSelectors.selectFirstNodeInWorkflow(workflowIdOrFirstGetNode, isGetNode),
          )
        : workflowIdOrFirstGetNode;

    return firstGetNode$.pipe(
      switchMap((getNode) => {
        let query: LogicalQueryNode | undefined;

        if (getNode?.datasetId && getNode.queryConditions?.length) {
          query = mapSimpleQueryConditionsToComplexFilters(
            getNode.queryConditions,
            LogicalType.And,
          );
        } else if (getNode?.datasetId && getNode.query) {
          query = getNode.query;
        }

        return combineLatest({
          query: of(query),
          nodeSchema: this.schemaService.obtainSchema(getNode?.datasetId || ''),
        });
      }),
      map(({ query, nodeSchema }) => {
        if (!query) {
          return [];
        }

        return getAllComparisonNodes(query)
          .filter((node) => !node.conditionId?.startsWith(SEARCH_AND_FILTERS_CONDITION_PREFIX))
          .map((node) => {
            let displayValue = node.value;
            if (node.displayInfos?.length) {
              displayValue = node.displayInfos
                ?.map((info) => {
                  return this.schemaService.replaceColumnAndTagIds(info.display);
                })
                .join(' ');
            }
            const column = this.schemaService.replaceColumnIdsWithNames(node.column);
            const conditionColumnIdOrName = node.column || '';
            const conditionColumn = findColumn(nodeSchema?.columns, conditionColumnIdOrName);
            const operator = this.iss.getComparisonOperatorName(node.operator, conditionColumn);
            return {
              column: node.column,
              description: `${column} ${operator} ${displayValue}`,
              displayInfos: node.displayInfos,
              isFormulaValue: !!node.isFormulaValue,
              operator: node.operator,
              original: node,
              value: node.value,
            };
          });
      }),
    );
  }

  /** Returns the data what is the actual source in the data source */
  getWorkflowDisplayData(workflow: Workflow): WorkflowDisplayData {
    const sourceNode = findWorkflowSourceNode(workflow);

    if (isGetNode(sourceNode)) {
      return {
        name: this.schemaService.getTableName(sourceNode.datasetId),
        modified: !!(
          sourceNode.subDataset ||
          sourceNode.sort ||
          sourceNode.maxResults ||
          sourceNode.queryConditions?.length ||
          sourceNode.query?.children?.length
        ),
        sourceNode,
      };
    } else if (isApiCallNode(sourceNode)) {
      return {
        name: 'API Call',
        sourceNode,
      };
    }

    return { name: 'Custom workflow', custom: true };
  }

  finishedEditingWorkflow(): Observable<{
    workflowId: string;
    projectId: string;
    workflow?: Workflow;
  }> {
    return this.actions$.pipe(ofType(runtimeActions.workflowEditDialogClosed));
  }

  hasWorkflowExecutionFailed(response?: ExecuteWorkflowResponse | ServerErrorResponse): boolean {
    if (!response) {
      return true;
    }
    return 'error' in response && !!response.error;
  }
}
